import {
  Box,
  Checkbox,
  CircularProgress,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material"
import type { FC } from "react"
import { useCallback } from "react"

import {
  InventoryLotTableHeaderCells,
  InventoryLotTableRowCells,
  InventoryPalletTableHeaderCells,
  InventoryPalletTableRowCells,
} from "../../../components"
import {
  ALL_LOTS_LOCKED_TOOLTIP,
  LOT_COLUMN_COUNT,
  LOT_LOCKED_TOOLTIP,
  LOT_TOGGLE_COLUMN_COUNT,
  PALLET_COLUMN_COUNT,
  PALLET_SEARCH_PLACEHOLDER,
  PALLET_TOGGLE_COLUMN_COUNT,
} from "../../../constants"
import { isLotLocked } from "../../../utils"

import SearchInput from "@/components/SearchInput/SearchInput"
import type {
  InventoryPalletGroup,
  InventoryPalletGroupsSortField,
  InventoryPalletLot,
  SortingOrder,
} from "@/graphql/codegen/graphql"

const SELECTION_MODES = {
  MULTI_LOT: "multiLot",
  SINGLE_PALLET: "singlePallet",
} as const

type SelectionMode = (typeof SELECTION_MODES)[keyof typeof SELECTION_MODES]

interface AdjustmentLotSelectionProps {
  palletGroups: InventoryPalletGroup[]
  isFetching: boolean
  selectedLotIds: Set<string>
  sortBy: InventoryPalletGroupsSortField
  sortDirection: SortingOrder
  uomName: string
  selectionMode?: SelectionMode
  onSelectionChange: (lotIds: Set<string>) => void
  onSortChange: (field: InventoryPalletGroupsSortField, direction: SortingOrder) => void
  onSearchChange: (search: string) => void
}

export const AdjustmentLotSelection: FC<AdjustmentLotSelectionProps> = ({
  palletGroups,
  isFetching,
  selectedLotIds,
  sortBy,
  sortDirection,
  uomName,
  selectionMode = SELECTION_MODES.MULTI_LOT,
  onSelectionChange,
  onSortChange,
  onSearchChange,
}) => {
  const isSinglePalletMode = selectionMode === SELECTION_MODES.SINGLE_PALLET
  const columnCount =
    PALLET_TOGGLE_COLUMN_COUNT +
    PALLET_COLUMN_COUNT +
    (isSinglePalletMode ? 0 : LOT_TOGGLE_COLUMN_COUNT) +
    LOT_COLUMN_COUNT

  const getPalletSelectionProps = useCallback(
    (pallet: InventoryPalletGroup) => {
      const selectableLots = pallet.lots.filter((lot) => !isLotLocked(lot))

      if (!selectableLots.length) {
        return { checked: false, indeterminate: false, disabled: true }
      }

      const selectedLotCount = selectableLots.filter((lot) => selectedLotIds.has(lot.rawInventoryId)).length

      return {
        checked: selectedLotCount === selectableLots.length,
        indeterminate: selectedLotCount > 0 && selectedLotCount < selectableLots.length,
        disabled: false,
      }
    },
    [selectedLotIds]
  )

  const handlePalletSelectionChange = useCallback(
    (pallet: InventoryPalletGroup) => {
      const selectableLots = pallet.lots.filter((lot) => !isLotLocked(lot))
      const isPalletSelected =
        selectableLots.length > 0 && selectableLots.every((lot) => selectedLotIds.has(lot.rawInventoryId))

      if (isSinglePalletMode) {
        onSelectionChange(isPalletSelected ? new Set() : new Set(selectableLots.map((lot) => lot.rawInventoryId)))
        return
      }

      const next = new Set(selectedLotIds)
      selectableLots.forEach((lot) =>
        isPalletSelected ? next.delete(lot.rawInventoryId) : next.add(lot.rawInventoryId)
      )
      onSelectionChange(next)
    },
    [isSinglePalletMode, selectedLotIds, onSelectionChange]
  )

  const handleLotSelectionChange = useCallback(
    (lot: InventoryPalletLot) => {
      const next = new Set(selectedLotIds)

      if (next.has(lot.rawInventoryId)) {
        next.delete(lot.rawInventoryId)
      } else {
        next.add(lot.rawInventoryId)
      }

      onSelectionChange(next)
    },
    [selectedLotIds, onSelectionChange]
  )

  return (
    <Box className='flex min-h-0 flex-1 flex-col rounded-lg border border-gray-400 p-4'>
      <Box className='mb-4 flex items-center justify-between'>
        <Typography variant='h6' className='font-bold leading-10'>
          {isSinglePalletMode ? "1. Select Pallet" : "1. Select Pallets and Lots"}
        </Typography>
        <SearchInput
          placeholder={PALLET_SEARCH_PLACEHOLDER}
          onChange={(e) => onSearchChange(e.target.value)}
          iconPosition='end'
          className='min-w-[360px]'
          data-testid='inventory.adjustment-lot-selection.search-field'
        />
      </Box>
      <Box className='relative flex min-h-0 flex-1'>
        {isFetching && (
          <Box className='absolute inset-0 top-[53px] z-10 flex items-center justify-center bg-white/60'>
            <CircularProgress size={48} />
          </Box>
        )}
        <TableContainer className='overflow-auto'>
          <Table stickyHeader>
            <TableHead className='bg-gray-100'>
              <TableRow>
                {/* Pallet checkbox column */}
                <TableCell className='w-12' />
                <InventoryPalletTableHeaderCells sort={{ sortBy, sortDirection, onSortChange }} />
                {/* Lot checkbox column */}
                {!isSinglePalletMode && <TableCell className='w-12' />}
                <InventoryLotTableHeaderCells sort={{ sortBy, sortDirection, onSortChange }} />
              </TableRow>
            </TableHead>

            <TableBody>
              {!palletGroups.length && (
                <TableRow>
                  <TableCell colSpan={columnCount} align='center' className='py-8'>
                    No pallets found
                  </TableCell>
                </TableRow>
              )}

              {palletGroups.map((pallet) => {
                const palletSelectionProps = getPalletSelectionProps(pallet)
                const isPalletDisabled = palletSelectionProps.disabled

                return pallet.lots.map((lot, lotIndex) => {
                  const isFirstLot = lotIndex === 0
                  const isLotDisabled = isLotLocked(lot)

                  return (
                    <TableRow
                      key={lot.rawInventoryId}
                      className={isLotDisabled ? "bg-gray-100" : undefined}
                      data-testid={`inventory.adjustment-lot-selection.row-${lot.rawInventoryId}`}
                    >
                      {/* Pallet selection — only on first lot */}
                      {isFirstLot ? (
                        <Tooltip title={ALL_LOTS_LOCKED_TOOLTIP} disableHoverListener={!isPalletDisabled}>
                          <TableCell>
                            {isSinglePalletMode ? (
                              <Radio
                                size='small'
                                checked={palletSelectionProps.checked}
                                disabled={isPalletDisabled}
                                onClick={() => handlePalletSelectionChange(pallet)}
                                data-testid={`inventory.adjustment-lot-selection.pallet-radio-${pallet.referenceNbr}`}
                              />
                            ) : (
                              <Checkbox
                                size='small'
                                {...palletSelectionProps}
                                disabled={isPalletDisabled}
                                onChange={() => handlePalletSelectionChange(pallet)}
                                data-testid={`inventory.adjustment-lot-selection.pallet-checkbox-${pallet.referenceNbr}`}
                              />
                            )}
                          </TableCell>
                        </Tooltip>
                      ) : (
                        <TableCell className='h-16' />
                      )}
                      <InventoryPalletTableRowCells pallet={pallet} isFirstLot={isFirstLot} uomName={uomName} />
                      {!isSinglePalletMode && (
                        <Tooltip title={LOT_LOCKED_TOOLTIP} disableHoverListener={!isLotDisabled}>
                          <TableCell>
                            <Checkbox
                              size='small'
                              checked={selectedLotIds.has(lot.rawInventoryId)}
                              disabled={isLotDisabled}
                              onChange={() => handleLotSelectionChange(lot)}
                              data-testid={`inventory.adjustment-lot-selection.lot-checkbox-${lot.rawInventoryId}`}
                            />
                          </TableCell>
                        </Tooltip>
                      )}
                      <InventoryLotTableRowCells lot={lot} uomName={uomName} />
                    </TableRow>
                  )
                })
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  )
}
